import type { RiskGapModel } from '@ctw/shared/api/fhir/models/risk-gap';
import { usePatientRiskGapsWithUIExclusions } from '@ctw/shared/api/fhir/risk-gaps';
import { Button } from '@ctw/shared/components/button';
import { withErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import { Heading } from '@ctw/shared/components/heading';
import { EmptyPatientTable } from '@ctw/shared/content/empty-patients-table';
import { PatientRiskGapsResourceTable } from '@ctw/shared/content/patient-risk-gaps/components/resource-table';
import { buildPatientRiskGapsColumnsV2 } from '@ctw/shared/content/patient-risk-gaps/helpers/columns-v2';
import { RiskGapFilters } from '@ctw/shared/content/patient-risk-gaps/helpers/filters';
import { PeriodDropdown } from '@ctw/shared/content/patient-risk-gaps/helpers/period-dropdown';
import {
  ListDetailViewWithDockableDrawer,
  useHasListDetailViewWithDockableDrawer,
  useListDetailViewDetailPanel,
  useListDetailViewHeader,
} from '@ctw/shared/content/patient-risk-gaps/layout/list-detail-view-with-dockable-drawer';
import { ResourceTableActions } from '@ctw/shared/content/resource/resource-table-actions';
import { useBreakpointQuery } from '@ctw/shared/hooks/breakpoints';
import { useFilteredSortedData } from '@ctw/shared/hooks/use-filtered-sorted-data';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { faArrowLeft, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo, useState } from 'react';
import { defaultRiskSort } from './helpers/sorts';

interface PatientRiskGapsV2Props {
  testId?: string;
}

function RiskGapDetailView({
  riskGap,
  onClose,
}: { riskGap: RiskGapModel | null; onClose: () => void }) {
  const { breakpoints } = useBreakpointQuery();

  return (
    <div
      className={twx('flex h-full max-h-full w-0 flex-col overflow-hidden bg-white', {
        'w-full max-w-full': riskGap,
      })}
    >
      <div
        className={tw`flex w-full flex-1 items-start justify-between gap-2 overflow-visible px-5 py-4`}
      >
        <Button
          ariaLabel={breakpoints.isAtLeast.lg ? 'Close' : 'Back'}
          type="button"
          size="sm"
          variant="circular"
          className={tw`flex min-w-8 items-center justify-center rounded-full bg-content-main p-2 hover:bg-content-subtle/25 active:bg-content-subtle/25`}
          onClick={onClose}
        >
          <FontAwesomeIcon icon={breakpoints.isAtLeast.lg ? faXmark : faArrowLeft} />
        </Button>
        <Heading level="h1" className={tw`line-clamp-2 flex font-medium `}>
          {riskGap?.title}
        </Heading>
      </div>
      <div className={tw`flex flex-1 overflow-auto p-4`}>
        {/* Empty content area - detail view content to be added */}
      </div>
    </div>
  );
}

function PatientRiskGapsV2Content({ testId }: { testId?: string }) {
  const risksQuery = usePatientRiskGapsWithUIExclusions();
  const [selectedRiskGap, setSelectedRiskGap] = useState<RiskGapModel | null>(null);

  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<string>(currentYear.toString());

  const patientRiskGapsColumns = useMemo(() => buildPatientRiskGapsColumnsV2(), []);

  const riskGapsFilteredByYear = useMemo(() => {
    if (!risksQuery.data) {
      return [];
    }
    return risksQuery.data.filter((riskGap) => {
      const periodYear = riskGap.period;
      return periodYear === selectedYear;
    });
  }, [risksQuery.data, selectedYear]);

  const { data, filters, setFilters } = useFilteredSortedData({
    cacheKey: 'patient-risk-gaps',
    defaultSort: defaultRiskSort,
    records: riskGapsFilteredByYear,
  });

  const hasZeroFilteredRecords = !risksQuery.isLoading && data.length === 0;

  useListDetailViewDetailPanel(
    selectedRiskGap ? (
      <RiskGapDetailView riskGap={selectedRiskGap} onClose={() => setSelectedRiskGap(null)} />
    ) : null,
    [selectedRiskGap],
  );

  const filtersKey = JSON.stringify(filters);
  useListDetailViewHeader(
    () => (
      <ResourceTableActions
        headAction={<PeriodDropdown selectedYear={selectedYear} onChange={setSelectedYear} />}
        filterOptions={{
          filters: RiskGapFilters,
          selected: filters,
          onChange: setFilters,
        }}
        className="w-full justify-between"
      />
    ),
    [filtersKey, selectedYear],
  );

  return (
    <PatientRiskGapsResourceTable
      title="Patient Risk Gaps"
      titleVisuallyHidden={false}
      showTableHead={true}
      isLoading={risksQuery.isLoading}
      stackedBreakpoint="sm"
      hasSelectedRow={selectedRiskGap !== null}
      columns={patientRiskGapsColumns}
      data={data}
      testId={testId}
      emptyMessage={
        <EmptyPatientTable
          hasZeroFilteredRecords={hasZeroFilteredRecords}
          resourceName="risk opportunities"
        />
      }
      onRowClick={({ record }) => setSelectedRiskGap(record)}
    />
  );
}

export const PatientRiskGapsV2 = withErrorBoundary<PatientRiskGapsV2Props>({
  boundaryName: 'PatientRiskGaps',
  Component: ({ testId }: PatientRiskGapsV2Props) => {
    const hasListDetailView = useHasListDetailViewWithDockableDrawer();
    if (hasListDetailView) {
      return <PatientRiskGapsV2Content testId={testId} />;
    }
    return (
      <ListDetailViewWithDockableDrawer>
        <PatientRiskGapsV2Content testId={testId} />
      </ListDetailViewWithDockableDrawer>
    );
  },
});
